/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'next-i18next'
import { showToast } from 'src/core/helpers'
import React, { useState, useEffect } from 'react'
import { Button, Divider, Form, Switch } from 'antd'
import styles from './DiscountVoucherForm.module.scss'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import { CheckOutlined, CloseOutlined, SendOutlined } from '@ant-design/icons'
import {
  resetCreateDiscountVoucherState,
  createDiscountVoucherSelector,
  createDiscountVoucher,
  getCampaings,
} from 'src/features'
import {
  DiscountVoucherFormPayload,
  DiscountVoucherApiPayload,
} from 'src/core/models'
import {
  CustomInputDecimal,
  CustomInputNumber,
  CustomTextInput,
  CustomModal,
  CustomText,
} from 'src/components'

interface DiscountVoucherFormProps {
  campaignCode: string
  closeModal: () => void
  isModalVisible: boolean
}

const DiscountVoucherForm: React.FC<DiscountVoucherFormProps> = (props) => {
  const { campaignCode, isModalVisible, closeModal } = props
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const [isLinkedToEmail, setIsLinkedToEmail] = useState<boolean>(false)
  const [isErrorVisible, setIsErrorVisible] = useState<boolean>(false)
  const [allowsMultiUse, setAllowsMultiUse] = useState<boolean>(true)

  const {
    loading: loadingCreate,
    success: successCreate,
    hasOnwerError
  } = useAppSelector(createDiscountVoucherSelector)

  const handleSubmit = (values: DiscountVoucherFormPayload) => {
    const payload: DiscountVoucherApiPayload = {
      ...values,
      campaign_code: campaignCode,
      linked_to_email: isLinkedToEmail,
      allows_multi_use: allowsMultiUse
    }
    setIsErrorVisible(false)
    dispatch(createDiscountVoucher(payload))
  }

  const handleCloseModal = () => {
    setIsLinkedToEmail(false)
    setIsErrorVisible(false)
    form.resetFields()
    closeModal()
  }

  useEffect(() => {
    if (successCreate) {
      handleCloseModal()
      showToast('success', t('Vouchers.VoucherCreated'), 3)
      dispatch(resetCreateDiscountVoucherState())
      dispatch(getCampaings())
    }
    if (hasOnwerError) {
      setIsErrorVisible(true)
      dispatch(resetCreateDiscountVoucherState())
    }
  }, [successCreate, hasOnwerError])

  return (
    <CustomModal
      title={t('Vouchers.Create2')}
      open={isModalVisible}
      onCancel={handleCloseModal}
      footer={null}
    >
      <Form form={form} onFinish={handleSubmit}>

        {/* Percentage */}
        <CustomText label={t('Vouchers.PercentageLabel')} />
        <CustomInputDecimal
          required
          name='percentage'
          placeholder={t('Vouchers.PercentagePlaceholder')}
          min={1}
          max={50}
        />

        {/* Validity in days */}
        <CustomText label={t('Vouchers.Days')} />
        <CustomInputNumber
          required
          name='validity_in_days'
          placeholder={t('Vouchers.DaysPlaceholder')}
          min={1}
          max={365}
        />

        <div className={styles.checkboxContent}>
          <CustomText label={t('Vouchers.MultiUse')} />
          <Switch
            checked={allowsMultiUse}
            onChange={(value) => setAllowsMultiUse(value)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </div>

        {/* Linked To Email Cehckbox */}
        <div className={styles.checkboxContent}>
          <CustomText label={t('Vouchers.EmailLinked')} />
          <Switch
            checked={isLinkedToEmail}
            onChange={(value) => setIsLinkedToEmail(value)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </div>

        {/* Email */}
        {isLinkedToEmail && <>
          <CustomText label={t('Summary.Email')} />
          <CustomTextInput
            required
            name='email'
            placeholder={t('admin.Brokerage.EmailPlaceHolder')}
            type='email'
          />
        </>}


        {/* Foooter labels */}
        <div className={styles.labels}>
          <CustomText label={t('Vouchers.CampaignCode')} />
          <CustomText label={campaignCode} mark />
        </div>

        <Divider />

        {/* Error Owner */}
        {isErrorVisible && (
          <div className='blink-animation mb-4'>
            <CustomText
              label={t('Vouchers.OnwerErrorX2')}
              type='danger'
            />
          </div>
        )}

        {/* Submit Button */}

        <Button
          type='primary'
          htmlType='submit'
          icon={<SendOutlined />}
          block
          loading={loadingCreate}
        >
          {loadingCreate ? t('Vouchers.Creating') : t('Vouchers.Create')}
        </Button>
      </Form>
    </CustomModal>
  )
}

export default DiscountVoucherForm
